import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Grid,
  Input,
  message,
  theme,
  Typography,
  Modal,
} from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import logo from "../../../Logo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import AWSCognitoUserPool from "../../../shared/api/AWSCognitoUserPool";
import { FbContext } from "../../../shared/rbac/context";

const { useToken } = theme;
const { useBreakpoint } = Grid;
const { Text, Title, Link, Paragraph } = Typography;

export default function LoginPage() {
  const [form] = Form.useForm();
  const { token } = useToken();
  const screens = useBreakpoint();
  const location = useLocation();
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const authCxt = useContext(FbContext);

  useEffect(() => {
    // Use authCxt.authenticated directly in the condition
    if (authCxt.authenticated) {
      navigate("/dashboard");
    }
  }, [authCxt.authenticated, navigate]); // Add authCxt.authenticated to dependency array

  useEffect(() => {
    if (location.state?.showSuccessModal) {
      Modal.success({
        title: (
          <Text
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#21130d",
            }}
          >
            Alright it's almost done!
          </Text>
        ),
        content: (
          <div
            style={{
              paddingTop: "10px",
              fontSize: "15px",
              lineHeight: "1.6",
            }}
          >
            <Paragraph>
              <Text strong>Thank you for registering!</Text>
            </Paragraph>
            <Paragraph>
              Your account will be activated shortly. We'll notify you once
              it's ready to use.
            </Paragraph>
            <Paragraph>
              <Text type="warning">Note:</Text> In case of any emergency,
              please contact us at{" "}
              <Text strong style={{ color: "#1677ff" }}>
                support@face-bot.ai
              </Text>
               . We will assist you as soon as possible.
            </Paragraph>
          </div>
        ),
        okText: "Got it!",
        centered: true,
        width: 600,
      });
      // Clear the state after showing modal
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleFinish = async(values) => {
    setLoading(true);
    const userData = {
      Username: values.email,
      Pool: AWSCognitoUserPool,
    };

    const cognitoUser = new CognitoUser(userData);

    const authenticationDetails = new AuthenticationDetails({
      Username: values.email,
      Password: values.password,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async (result) => {
        try {
          await authCxt.handleAuthentication(result);
          message.success("Login successful!");
          navigate("/dashboard", { replace: true });
        } catch (error) {
          console.error("Auth handling error:", error);
          message.error("Authentication failed");
        } finally {
          setLoading(false);
        }
      },
      onFailure: (err) => {
        console.log("Login error:", err);

        setLoading(false);
        // Handle specific error messages
        if (err.code === "UserNotFoundException") {
          message.error("User not found. Please check your email.");
        } else if (err.code === "NotAuthorizedException") {
          message.error("Incorrect password. Please try again.");
        } else if (err.code === "UserNotConfirmedException") {
          message.error(
            "User not confirmed. Please check your email for confirmation."
          );
        } else if (err.code === "InvalidLambdaResponseException") {
          Modal.success({
            title: (
              <Text
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#21130d",
                }}
              >
                Alright it's almost done!
              </Text>
            ),
            content: (
              <div
                style={{
                  paddingTop: "10px",
                  fontSize: "15px",
                  lineHeight: "1.6",
                }}
              >
                <Paragraph>
                  <Text strong>Thank you for registering!</Text>
                </Paragraph>
                <Paragraph>
                  Your account will be activated shortly. We'll notify you once
                  it's ready to use.
                </Paragraph>
                <Paragraph>
                  <Text type="warning">Note:</Text> In case of any emergency,
                  please contact us at{" "}
                  <Text strong style={{ color: "#1677ff" }}>
                    support@face-bot.ai
                  </Text>
                  . We will assist you as soon as possible.
                </Paragraph>
              </div>
            ),
            okText: "Got it!",
            centered: true,
            width: 600,
          });
        }
      },
    });
  };

  const handleEnterKey = (event) => {
    if (event.keyCode === 13) {
      form.submit();
    }
  };

  const styles = {
    container: {
      margin: "0 auto",
      padding: screens.md
        ? `${token.paddingXL}px`
        : `${token.sizeXXL}px ${token.padding}px`,
      width: "380px",
    },
    footer: {
      marginTop: token.marginLG,
      textAlign: "center",
      width: "100%",
    },
    forgotPassword: {
      marginTop: "-50px",
      float: "right",
    },
    header: {
      marginBottom: token.marginXL,
      textAlign: "center",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    section: {
      alignItems: "center",
      backgroundColor: token.colorBgContainer,
      display: "flex",
      padding: screens.md ? `${token.sizeXXL}px 0px` : "0px",
    },
    text: {
      color: token.colorTextSecondary,
    },
    title: {
      fontSize: screens.md ? token.fontSizeHeading2 : token.fontSizeHeading3,
    },
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <img src={logo} alt="logo" />

          <Title style={styles.title}>Sign in</Title>
          <Text style={styles.text}>
            Welcome back to FaceBot! Please enter your details below to sign in.
          </Text>
        </div>
        <Form onFinish={handleFinish} layout="vertical" name="normal_login" initialValues={{remember: true}} form={form} requiredMark = "optional">
          <Form.Item name="email" rules={[{ required: true, type: "email", message: "Please input your Email!" }]}>
            <Input placeholder="Email" prefix={<MailOutlined/>} type="email" size="large"/>
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: "Please input your Password!"}]}>
            <Input.Password placeholder="Password" prefix={<LockOutlined/>} type="password" size="large" />
          </Form.Item>
          <Form.Item>
          </Form.Item>
          <Form.Item>
            <a style={styles.forgotPassword} href="/forgot-password">
              Forgot password?
            </a>
          </Form.Item>
          <Form.Item style={{ marginBottom: "0px", marginTop: "-50px" }}>
            <Button
              block="true"
              loading={loading}
              size="large"
              type="primary"
              htmlType="submit"
            >
              Log in
            </Button>
            <div style={styles.footer}>
              <Text style={styles.text}>Don't have an account?</Text>{" "}
              <Link href="/register">Sign up now</Link>
            </div>
          </Form.Item>
        </Form>
      </div>
    </section>
  );
}
